/**
 * Hired, and nobody can say whether they have started.
 *
 * These rows have a name and an offer but no actual start date in the Hiring
 * Database, so the board cannot tell a colleague who has been at work since May
 * from one arriving in November. Against the September export that is 23 roles,
 * 21 of them with an offer-accepted date and plan dates going back months — so
 * most are people already here whose first day nobody wrote down.
 *
 * It is a LIST rather than a chip on each row because the fix is not on the
 * board: somebody opens Airtable and fills the column in. A gap you have to go
 * looking for is one that stays open, and the FTE of every unit holding one is
 * wrong until it closes.
 *
 * SCOPED TO THE YEAR BEING PLANNED (Theresa, Sep 15 2026). Unbounded it is 42
 * roles, 19 of them planned for 2025 — the Hiring Database goes back years and
 * its older rows were never kept to this standard, so the list was mostly
 * archaeology and read as an unfixable backlog. The filter is in `linkHires`,
 * not here: the count on the collapsed header has to mean the same thing as the
 * list inside it.
 */
import { useState } from 'react';
import type { HireFixup } from '../../../types/taDemand';
import { Caret } from './demandUi';

export function FixMeList({ items }: { items: HireFixup[] }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <Caret open={open} />
        <span className="text-[12px] font-semibold text-amber-900">
          {items.length} hired {items.length === 1 ? 'role has' : 'roles have'} no start date
        </span>
        <span className="text-[11px] text-amber-700">
          — the board cannot tell whether these colleagues have started
        </span>
      </button>
      {open && (
        <ul className="border-t border-amber-200 px-3 py-2">
          {items.map((f) => (
            <li key={f.roleId} className="flex flex-wrap items-baseline gap-x-2 py-0.5 text-[12px]">
              <span className="font-medium text-gray-900">{f.name}</span>
              <span className="text-gray-600">{f.title}</span>
              <span className="text-gray-400">
                {f.unit}
                {f.offerAcceptedMonth ? ` · offer accepted ${f.offerAcceptedMonth}` : ''}
                {f.plannedMonth ? ` · planned ${f.plannedMonth}` : ''}
              </span>
            </li>
          ))}
          <li className="mt-1.5 border-t border-amber-200 pt-1.5 text-[11px] text-amber-800">
            Fix in the Hiring Database: fill in <b>Actual Start Date</b>. Until then these roles
            stay off their unit&rsquo;s current-team FTE. Roles planned for earlier years are not
            listed &mdash; older rows were never kept to this standard, and a list nobody can
            finish is one nobody starts.
          </li>
        </ul>
      )}
    </div>
  );
}
